<form [formGroup]="fixedDepositProductInterestRateChartForm">
  <div class="layout-row-wrap gap-2px responsive-column">
    <h4 class="mat-h3 flex-83">
      {{ 'labels.inputs.Interest Rate Chart' | translate }}
      <i class="fas fa-question" matTooltip="{{ 'tooltips.The interest rate chart' | translate }}"></i>
    </h4>

    <div class="flex-13 layout-row align-start-center">
      <button type="button" mat-raised-button color="primary" (click)="addChart()">
        <fa-icon icon="plus" class="m-r-10"></fa-icon>
        {{ 'labels.buttons.Add' | translate }}
      </button>
    </div>

    @for (chart of charts.controls; track chart; let chartIndex = $index) {
      <div class="flex-fill layout-row-wrap responsive-column" formArrayName="charts">
        <mat-divider class="flex-98"></mat-divider>
        <div class="flex-fill layout-row-wrap responsive-column" [formGroupName]="chartIndex">
          <div class="flex-100" align="end">
            <button
              type="button"
              mat-icon-button
              color="warn"
              (click)="delete(charts, chartIndex)"
              matTooltip="{{ 'tooltips.Delete Interest Rate Chart' | translate }}"
              matTooltipPosition="left"
            >
              <fa-icon icon="trash"></fa-icon>
            </button>
          </div>
          <mat-form-field class="flex-28 m-r-5">
            <mat-label>{{ 'labels.inputs.Name' | translate }}</mat-label>
            <input matInput formControlName="name" required />
            @if (chart.get('name')?.touched && chart.get('name')?.hasError('required')) {
              <mat-error>
                {{ 'labels.inputs.Name' | translate }} {{ 'labels.commons.is' | translate }}
                <strong>{{ 'labels.commons.required' | translate }}</strong>
              </mat-error>
            }
          </mat-form-field>
          <mat-form-field class="flex-70">
            <mat-label>{{ 'labels.inputs.Description' | translate }}</mat-label>
            <textarea matInput formControlName="description" required></textarea>
            @if (chart.get('description')?.touched && chart.get('description')?.hasError('required')) {
              <mat-error>
                {{ 'labels.inputs.Description' | translate }} {{ 'labels.commons.is' | translate }}
                <strong>{{ 'labels.commons.required' | translate }}</strong>
              </mat-error>
            }
          </mat-form-field>
          <mat-form-field class="flex-48 m-r-5" (click)="validFromDatePicker.open()">
            <mat-label>{{ 'labels.inputs.From Date' | translate }}</mat-label>
            <input
              matInput
              [min]="minDate"
              [max]="maxDate"
              [matDatepicker]="validFromDatePicker"
              formControlName="fromDate"
              required
            />
            <mat-datepicker-toggle matSuffix [for]="validFromDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #validFromDatePicker></mat-datepicker>
            <mat-error>
              {{ 'labels.inputs.Valid from Date' | translate }} {{ 'labels.commons.is' | translate }}
              <strong>{{ 'labels.commons.required' | translate }}</strong>
            </mat-error>
          </mat-form-field>
          <mat-form-field class="flex-48 m-r-5" (click)="endDatePicker.open()">
            <mat-label>{{ 'labels.inputs.End Date' | translate }}</mat-label>
            <input matInput [min]="minDate" [max]="maxDate" [matDatepicker]="endDatePicker" formControlName="endDate" />
            <mat-datepicker-toggle matSuffix [for]="endDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #endDatePicker></mat-datepicker>
          </mat-form-field>
          <div class="flex-100 layout-row align-between align-items-center">
            <mat-checkbox labelPosition="before" formControlName="isPrimaryGroupingByAmount">
              {{ 'labels.inputs.Is primary grouping by amount?' | translate }}
            </mat-checkbox>
          </div>
          <div class="center flex-100">
            <button type="button" mat-raised-button color="primary" (click)="addChartSlab(chart.controls.chartSlabs)">
              <fa-icon icon="plus" class="m-r-10"></fa-icon>
              {{ 'labels.buttons.Add Slab' | translate }}
            </button>
          </div>
          @if (chart.value.chartSlabs.length === 0) {
            <div class="flex-100">
              <h3 class="mat-h3">{{ 'labels.heading.It is required to add at least one Slab' | translate }}</h3>
            </div>
          }
          @if (chart.value.chartSlabs.length !== 0) {
            <table
              class="flex-98 mat-elevation-z1"
              mat-table
              [dataSource]="chart.value.chartSlabs"
              multiTemplateDataRows
            >
              <ng-container matColumnDef="period">
                <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Period' | translate }}</th>
                <td mat-cell *matCellDef="let chartSlab">
                  {{
                    chartSlab.fromPeriod +
                      ' - ' +
                      chartSlab.toPeriod +
                      ' ' +
                      (chartSlab.periodType | find: periodTypeData : 'id' : 'value')
                  }}
                </td>
              </ng-container>
              <ng-container matColumnDef="amountRange">
                <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Amount Range' | translate }}</th>
                <td mat-cell *matCellDef="let chartSlab">
                  {{ chartSlab.amountRangeFrom + ' - ' + chartSlab.amountRangeTo }}
                </td>
              </ng-container>
              <ng-container matColumnDef="annualInterestRate">
                <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Interest' | translate }}</th>
                <td mat-cell *matCellDef="let chartSlab">{{ chartSlab.annualInterestRate }} %</td>
              </ng-container>
              <ng-container matColumnDef="description">
                <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Description' | translate }}</th>
                <td mat-cell *matCellDef="let chartSlab">
                  {{ chartSlab.description }}
                </td>
              </ng-container>
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Actions' | translate }}</th>
                <td mat-cell *matCellDef="let chartSlab; let chartSlabIndex = dataIndex">
                  <button
                    mat-icon-button
                    color="primary"
                    (click)="editChartSlab(chart.controls.chartSlabs, chartSlabIndex)"
                  >
                    <fa-icon icon="edit"></fa-icon>
                  </button>
                  <button mat-icon-button color="warn" (click)="delete(chart.controls.chartSlabs, chartSlabIndex)">
                    <fa-icon icon="trash"></fa-icon>
                  </button>
                  <button
                    mat-button
                    color="primary"
                    (click)="
                      expandChartSlabIndex[chartIndex] =
                        expandChartSlabIndex[chartIndex] === chartSlabIndex ? null : chartSlabIndex
                    "
                  >
                    @if (expandChartSlabIndex[chartIndex] !== chartSlabIndex) {
                      <span>
                        <fa-icon icon="eye" class="m-r-10"></fa-icon>
                        {{ 'labels.buttons.View Incentives' | translate }}
                      </span>
                    }
                    @if (expandChartSlabIndex[chartIndex] === chartSlabIndex) {
                      <span>
                        <fa-icon icon="eye-slash" class="m-r-10"></fa-icon>
                        {{ 'labels.buttons.Hide Incentives' | translate }}
                      </span>
                    }
                  </button>
                </td>
              </ng-container>
              <ng-container matColumnDef="incentives">
                <td
                  mat-cell
                  *matCellDef="let chartSlab; let chartSlabIndex = dataIndex"
                  [attr.colspan]="chartSlabsDisplayedColumns[chartIndex].length"
                >
                  <div
                    class="layout-row-wrap flex-fill incentives"
                    [@expandChartSlab]="chartSlabIndex === expandChartSlabIndex[chartIndex] ? 'expanded' : 'collapsed'"
                  >
                    <mat-card class="layout-row-wrap flex-fill">
                      <h4 class="mat-h4 flex-63">
                        {{ 'labels.heading.Incentives' | translate }}
                      </h4>
                      <div class="flex-13">
                        <button
                          mat-raised-button
                          color="primary"
                          (click)="addIncentive(getIncentives(chart.controls.chartSlabs, chartSlabIndex))"
                        >
                          <fa-icon icon="plus" class="m-r-10"></fa-icon>
                          {{ 'labels.buttons.Add' | translate }}
                        </button>
                      </div>
                      @if (chartSlab.incentives.length) {
                        <table class="mat-elevation-z1 flex-fill" mat-table [dataSource]="chartSlab.incentives">
                          <ng-container matColumnDef="entityType">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Entity Type' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive">
                              {{ incentive.entityType | find: entityTypeData : 'id' : 'value' }}
                            </td>
                          </ng-container>
                          <ng-container matColumnDef="attributeName">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Attribute Name' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive">
                              {{ incentive.attributeName | find: attributeNameData : 'id' : 'value' }}
                            </td>
                          </ng-container>
                          <ng-container matColumnDef="conditionType">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Condition Type' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive">
                              {{ incentive.conditionType | find: conditionTypeData : 'id' : 'value' }}
                            </td>
                          </ng-container>
                          <ng-container matColumnDef="attributeValue">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Attribute Value' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive">
                              @switch (incentive.attributeName) {
                                @case (2) {
                                  <span>{{ incentive.attributeValue | find: genderData : 'id' : 'name' }}</span>
                                }
                                @case (3) {
                                  <span>{{ incentive.attributeValue }}</span>
                                }
                                @case (4) {
                                  <span>{{ incentive.attributeValue | find: clientTypeData : 'id' : 'name' }}</span>
                                }
                                @case (5) {
                                  <span>{{
                                    incentive.attributeValue | find: clientClassificationData : 'id' : 'name'
                                  }}</span>
                                }
                              }
                            </td>
                          </ng-container>
                          <ng-container matColumnDef="incentiveType">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Incentive Type' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive">
                              {{ incentive.incentiveType | find: incentiveTypeData : 'id' : 'value' }}
                            </td>
                          </ng-container>
                          <ng-container matColumnDef="amount">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Interest' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive">
                              {{ incentive.amount }}
                            </td>
                          </ng-container>
                          <ng-container matColumnDef="actions">
                            <th mat-header-cell *matHeaderCellDef>{{ 'labels.inputs.Actions' | translate }}</th>
                            <td mat-cell *matCellDef="let incentive; let incentiveIndex = index">
                              <button
                                mat-icon-button
                                color="primary"
                                (click)="
                                  editIncentive(
                                    getIncentives(chart.controls.chartSlabs, chartSlabIndex),
                                    incentiveIndex
                                  )
                                "
                              >
                                <fa-icon icon="edit"></fa-icon>
                              </button>
                              <button
                                mat-icon-button
                                color="warn"
                                (click)="
                                  delete(getIncentives(chart.controls.chartSlabs, chartSlabIndex), incentiveIndex)
                                "
                              >
                                <fa-icon icon="trash"></fa-icon>
                              </button>
                            </td>
                          </ng-container>
                          <tr mat-header-row *matHeaderRowDef="incentivesDisplayedColumns"></tr>
                          <tr mat-row *matRowDef="let row; columns: incentivesDisplayedColumns"></tr>
                        </table>
                      }
                    </mat-card>
                  </div>
                </td>
              </ng-container>
              <tr mat-header-row *matHeaderRowDef="chartSlabsDisplayedColumns[chartIndex]"></tr>
              <tr mat-row *matRowDef="let row; columns: chartSlabsDisplayedColumns[chartIndex]"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: chartSlabsIncentivesDisplayedColumns"
                class="incentives-row"
              ></tr>
            </table>
          }
        </div>
      </div>
    }
  </div>

  <div class="layout-row align-center gap-2percent margin-t responsive-column">
    <button mat-raised-button matStepperPrevious>
      <fa-icon icon="arrow-left" class="m-r-10"></fa-icon>
      {{ 'labels.buttons.Previous' | translate }}
    </button>
    <button mat-raised-button matStepperNext>
      {{ 'labels.buttons.Next' | translate }}
      <fa-icon icon="arrow-right" class="m-l-10"></fa-icon>
    </button>
  </div>
</form>
